export type RuleOperator = "==" | "!=" | ">" | "<" | ">=" | "<=" | "in";

export interface Rule {
  type: "rule";
  id: string;
  field: string;
  operator: RuleOperator;
  value: string;
}

export interface RuleGroup {
  type: "group";
  id: string;
  combinator: "and" | "or";
  rules: (Rule | RuleGroup)[];
}

interface RuleBuilderProps {
  group: RuleGroup;
  onChange: (group: RuleGroup) => void;
  availableFields?: string[];
}

const generateId = () => Math.random().toString(36).substring(2, 9);

export function createEmptyGroup(): RuleGroup {
  return { type: "group", id: generateId(), combinator: "and", rules: [] };
}

export function createEmptyRule(): Rule {
  return { type: "rule", id: generateId(), field: "", operator: "==", value: "" };
}

export function ruleGroupToJsonLogic(group: RuleGroup | null): Record<string, any> | undefined {
  if (!group || group.rules.length === 0) return undefined;

  const processNode = (node: Rule | RuleGroup): Record<string, any> | null => {
    if (node.type === "rule") {
      if (!node.field) return null;
      let val: any = node.value;
      if (val === "true") val = true;
      else if (val === "false") val = false;
      else if (!isNaN(Number(val)) && val !== "") val = Number(val);

      if (node.operator === "in") {
        const arr = String(val).split(",").map((s) => s.trim()).filter(Boolean);
        return { in: [{ var: node.field }, arr] };
      }
      return { [node.operator]: [{ var: node.field }, val] };
    } else {
      const validRules = node.rules.map(processNode).filter(Boolean);
      if (validRules.length === 0) return null;
      if (validRules.length === 1) return validRules[0]!;
      return { [node.combinator]: validRules };
    }
  };

  const res = processNode(group);
  return res ? res : undefined;
}

export function jsonLogicToRuleGroup(logic: Record<string, any> | undefined | null): RuleGroup {
  const root = createEmptyGroup();
  if (!logic || Object.keys(logic).length === 0) return root;

  const parseNode = (node: Record<string, any>): Rule | RuleGroup | null => {
    const keys = Object.keys(node);
    if (keys.length === 0) return null;
    const op = keys[0];
    if (!op) return null;
    const args = node[op];

    if (op === "and" || op === "or") {
      if (!Array.isArray(args)) return null;
      const children = args.map(parseNode).filter(Boolean) as (Rule | RuleGroup)[];
      return { type: "group", id: generateId(), combinator: op as "and" | "or", rules: children };
    } else {
      // Rule
      if (!Array.isArray(args) || args.length < 2) return null;
      const varObj = args[0];
      const val = args[1];
      if (typeof varObj !== "object" || !varObj["var"]) return null;
      
      let strVal = "";
      if (op === "in" && Array.isArray(val)) {
        strVal = val.join(", ");
      } else {
        strVal = String(val);
      }
      return {
        type: "rule",
        id: generateId(),
        field: varObj["var"],
        operator: op as RuleOperator,
        value: strVal,
      };
    }
  };

  const parsed = parseNode(logic);
  if (!parsed) return root;
  
  if (parsed.type === "group") {
    return parsed;
  } else {
    root.rules = [parsed];
    return root;
  }
}

export function RuleBuilder({ group, onChange, availableFields = [] }: RuleBuilderProps) {
  const updateCombinator = (combinator: "and" | "or") => {
    onChange({ ...group, combinator });
  };

  const addRule = () => {
    onChange({ ...group, rules: [...group.rules, createEmptyRule()] });
  };

  const addGroup = () => {
    onChange({ ...group, rules: [...group.rules, createEmptyGroup()] });
  };

  const updateChild = (id: string, newChild: Rule | RuleGroup | null) => {
    const newRules = group.rules.map((r) => {
      if (r.id === id) return newChild;
      return r;
    }).filter(Boolean) as (Rule | RuleGroup)[];
    onChange({ ...group, rules: newRules });
  };

  const removeChild = (id: string) => {
    onChange({ ...group, rules: group.rules.filter((r) => r.id !== id) });
  };

  return (
    <div style={{ border: "1px solid var(--color-border, #E4DCC8)", borderRadius: "10px", padding: "12px", background: "#FFFFFF", display: "flex", flexDirection: "column", gap: "12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", borderBottom: "1px solid var(--color-border, #E4DCC8)", paddingBottom: "8px" }}>
        <select
          value={group.combinator}
          onChange={(e) => updateCombinator(e.target.value as "and" | "or")}
          style={{ fontSize: "0.85rem", background: "rgba(0,0,0,0.04)", border: "1px solid var(--color-border, #E4DCC8)", borderRadius: "8px", padding: "6px 10px", outline: "none", cursor: "pointer", fontWeight: 700, color: "#2B2B2B" }}
        >
          <option value="and">وَ (AND)</option>
          <option value="or">یـا (OR)</option>
        </select>
        <button
          type="button"
          onClick={addRule}
          style={{ fontSize: "0.85rem", color: "#10B981", fontWeight: 700, background: "rgba(16, 185, 129, 0.1)", border: "1px solid rgba(16, 185, 129, 0.2)", borderRadius: "8px", padding: "6px 12px", cursor: "pointer", transition: "all 0.2s" }}
        >
          + افزودن شرط
        </button>
        <button
          type="button"
          onClick={addGroup}
          style={{ fontSize: "0.85rem", color: "#3B82F6", fontWeight: 700, background: "rgba(59, 130, 246, 0.1)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", padding: "6px 12px", cursor: "pointer", transition: "all 0.2s" }}
        >
          + افزودن گروه
        </button>
      </div>

      {group.rules.length === 0 && (
        <div style={{ fontSize: "0.85rem", color: "#888", fontStyle: "italic", padding: "8px 0" }}>هیچ شرطی وجود ندارد.</div>
      )}

      <div style={{ paddingRight: "16px", borderRight: "2px solid var(--color-border, #E4DCC8)", paddingLeft: "16px", display: "flex", flexDirection: "column", gap: "8px" }}>
        {group.rules.map((child) => (
          <div key={child.id} style={{ position: "relative" }}>
            {child.type === "group" ? (
              <div style={{ paddingTop: "4px" }}>
                <RuleBuilder
                  group={child}
                  onChange={(newG) => updateChild(child.id, newG)}
                  availableFields={availableFields}
                />
                <button
                  type="button"
                  onClick={() => removeChild(child.id)}
                  style={{ position: "absolute", top: "8px", left: "8px", fontSize: "0.8rem", color: "#EF4444", background: "rgba(239, 68, 68, 0.1)", border: "none", borderRadius: "6px", padding: "4px 8px", cursor: "pointer", fontWeight: 700 }}
                >
                  حذف گروه
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", background: "rgba(0,0,0,0.02)", padding: "10px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", position: "relative" }}>
                <input
                  type="text"
                  list="available-fields"
                  style={{ fontSize: "0.85rem", border: "1px solid var(--color-border, #E4DCC8)", borderRadius: "8px", padding: "6px 10px", width: "130px", outline: "none", direction: "ltr" }}
                  placeholder="فیلد (مثلاً weight)"
                  value={child.field}
                  onChange={(e) => updateChild(child.id, { ...child, field: e.target.value })}
                />
                <select
                  style={{ fontSize: "0.85rem", border: "1px solid var(--color-border, #E4DCC8)", borderRadius: "8px", padding: "6px 10px", outline: "none", cursor: "pointer" }}
                  value={child.operator}
                  onChange={(e) => updateChild(child.id, { ...child, operator: e.target.value as RuleOperator })}
                >
                  <option value="==">برابر است با</option>
                  <option value="!=">مخالف است با</option>
                  <option value=">">بزرگتر از</option>
                  <option value="<">کوچکتر از</option>
                  <option value=">=">بزرگتر مساوی</option>
                  <option value="<=">کوچکتر مساوی</option>
                  <option value="in">شامل (جدا شده با کاما)</option>
                </select>
                <input
                  type="text"
                  style={{ fontSize: "0.85rem", border: "1px solid var(--color-border, #E4DCC8)", borderRadius: "8px", padding: "6px 10px", flex: 1, minWidth: "100px", outline: "none" }}
                  placeholder="مقدار (true, false, عددی، متنی)"
                  value={child.value}
                  onChange={(e) => updateChild(child.id, { ...child, value: e.target.value })}
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => removeChild(child.id)}
                  style={{ color: "#EF4444", background: "none", border: "none", padding: "0 6px", fontSize: "1.2rem", cursor: "pointer", fontWeight: "bold" }}
                  title="حذف شرط"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      <datalist id="available-fields">
        {availableFields.map((f) => (
          <option key={f} value={f} />
        ))}
        <option value="material_type" />
      </datalist>
    </div>
  );
}
